import { useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { goals } from '@/data/products';
import { GOAL } from '@/constants/testIds';

export default function ShopByGoal() {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('[data-goal-card]');
    const step = card ? card.getBoundingClientRect().width + 20 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const goTo = () => {
    document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="goal"
      data-testid={GOAL.root}
      className="relative py-24 md:py-32 bg-[#F3EAD3]/60"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-4">Shop by Goal</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1D7448] font-light leading-[1.05]">
              What does your body <span className="italic">need today?</span>
            </h2>
          </motion.div>

          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid={GOAL.prev}
              onClick={() => scrollBy(-1)}
              aria-label="Previous"
              className="w-12 h-12 rounded-full border border-[#1D7448]/30 text-[#1D7448] hover:bg-[#1D7448] hover:text-[#FAF4E3] transition-colors flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              data-testid={GOAL.next}
              onClick={() => scrollBy(1)}
              aria-label="Next"
              className="w-12 h-12 rounded-full border border-[#1D7448]/30 text-[#1D7448] hover:bg-[#1D7448] hover:text-[#FAF4E3] transition-colors flex items-center justify-center"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid={GOAL.scroller}
        className="scrollbar-hide overflow-x-auto"
      >
        <div className="flex gap-5 md:gap-6 px-6 md:px-10 lg:px-24 pb-4">
          {goals.map((g, i) => (
            <motion.button
              key={g.slug}
              data-goal-card
              data-testid={GOAL.card(g.slug)}
              onClick={goTo}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group relative shrink-0 w-[260px] md:w-[300px] aspect-[3/4] rounded-[26px] overflow-hidden text-left"
            >
              <img
                src={g.image}
                alt={g.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.08]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0D3A24]/10 via-[#0D3A24]/30 to-[#0D3A24]/75" />

              <div className="relative h-full flex flex-col justify-end p-6 text-[#FAF4E3]">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#FAF4E3]/70 mb-2">Goal</p>
                <h3 className="font-serif text-3xl leading-tight">{g.title}</h3>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  Shop the ritual <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
